import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';

import * as moment from 'moment';

import CONSTANTS from '../../_constants/constants';

import { SharedModule } from '../../_shared/shared.module';
import { AlertService } from '../../_shared/services/alert.service';
import { UtilService } from '../../_shared/services/util.service';
import { DateService } from '../../_shared/services/date.service';
import { SessionService } from '../../_shared/services/session.service';

import { DateFormatPipe } from '../../_shared/pipes/date-format.pipe';

import { Season } from '../shared/season.model';
import { SeasonModalService } from '../shared/season-modal.service';
import { SeasonService } from '../shared/season.service';

import { LocaleConfig } from 'ngx-daterangepicker-material';

@Component({
  selector: 'app-season-detail',
  templateUrl: './season-detail.component.html',
  standalone: true,
  imports: [SharedModule, DateFormatPipe],
})
export class SeasonDetailComponent implements OnInit {
  @Input() season: Season;
  @Input() mode: string;
  @Input() defaultLanguage;
  @Input() additionalLanguages;
  @Input() isModal = false;

  @Output() private savedSeason = new EventEmitter<Season>();
  @Output() private canceledSeason = new EventEmitter();

  form: FormGroup;
  formSubmited = false;
  sendingForm = false;
  serverErrors = [];

  createMode = false;
  editMode = false;
  viewMode = false;

  dateDisplay = CONSTANTS.DATE_DISPLAY;
  dateFormat = CONSTANTS.DATE_FORMAT;
  today = CONSTANTS.TODAY_DATE;

  outDatedSeasons = false;

  calendarLocale: LocaleConfig;

  private seasonCopy = null;
  private dateFormatDB = CONSTANTS.DATE_FORMAT_DB;
  private rawDateInputs = {
    from: '',
    to: ''
  };

  constructor(
    private seasonService: SeasonService,
    private seasonModalService: SeasonModalService,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private utilService: UtilService,
    private dateService: DateService,
    private sessionService: SessionService
  ) {}

  ngOnInit() {
    // Set date format based on user language
    const userLanguageCode = this.sessionService.getUserLanguage().code;
    this.dateFormat = userLanguageCode === 'es' ? 'DD/MM/YYYY' : CONSTANTS.DATE_FORMAT;
    
    this.setSeasonForm();
    this.setDatePickers();

    if (this.season.id) {
      this.form.patchValue(this.season);

      this.setItemOptions(this.season, this.mode);
    } else {
      this.createMode = true;
    }
  }

  // Date validator function
  private dateValidator(format: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Don't validate empty values (use Validators.required for that)
      }

      // If value is object from date picker, it's valid
      if (typeof control.value === 'object' && control.value.startDate) {
        return null;
      }

      // If value is string, validate the format
      if (typeof control.value === 'string') {
        const dateStr = control.value.trim();
        
        if (dateStr.length !== 10) {
          return { invalidDate: true };
        }

        const parsedDate = moment(dateStr, format, true);
        
        if (!parsedDate.isValid()) {
          return { invalidDate: true };
        }
      }

      return null;
    };
  }

  // Referenced from app-translation-options
  onOpenedTranslation(language) {
    this.seasonModalService.openSeasonTranslation(this.season, language);
  }

  // Referenced from app-item-detail-form-actions
  onSavedSeason() {
    this.formSubmited = true;

    if (this.form.invalid) {
      this.serverErrors = [];

      return;
    }

    const formData = this.form.value;

    // Convert to yyyy-mm-dd
    formData.from = this.dateService.format(formData.from.startDate, this.dateFormatDB);
    formData.to = this.dateService.format(formData.to.startDate, this.dateFormatDB);

    // console.log(formData);
    // console.log(this.seasonCopy);

    const fields = ['name', 'from', 'to'];

    if (this.editMode && !fields.some((field) => formData[field] !== this.seasonCopy[field])) {
      this.onCanceled();
      return;
    }

    // console.log(formData);
    // return;

    this.sendingForm = true;

    this.seasonService.saveSeason(this.season.id, formData, this.defaultLanguage.lang).subscribe({
      next: (response) => {
        let msg = '';

        if (this.editMode) {
          msg = 'SEASON_UPDATED';

          // needed if we want to keep the page opened or if used in page
          // this.season = response;
          // this.setItemOptions(response);
        } else {
          msg = 'SEASON_CREATED';
          // this.season = response; // reload item - needed ?
        }

        this.alertService.open(msg);

        this.sendingForm = false;

        this.savedSeason.emit(response);
      },
      error: (response) => {
        this.serverErrors = this.utilService.setFormServerErrors(response.error.errors);

        this.sendingForm = false;
      },
    });
  }

  // Referenced from app-item-detail-form-actions
  onCanceled() {
    // if (this.editMode) {
    //   this.cancelEdit();
    //   return;
    // }

    this.canceledSeason.emit();
  }

  // Referenced from app-item-detail-form-actions
  onEditedSeason() {
    this.editMode = true;
    this.viewMode = false;
  }

  onDateInputCapture(fieldName: string, event: any) {
    // Capture the raw input value before the date picker modifies it
    this.rawDateInputs[fieldName] = event.target.value;
  }

  onDateInput(fieldName: string, event: any) {
    // Use the raw captured value instead of the potentially modified value
    const inputValue = this.rawDateInputs[fieldName] || event.target.value;
    
    // If empty, just update validation
    if (!inputValue || inputValue.trim() === '') {
      this.form.controls[fieldName].updateValueAndValidity();
      return;
    }
    
    const trimmedValue = inputValue.trim();
    
    // Check if input matches the date format (10 characters)
    if (trimmedValue.length === 10) {
      const parsedDate = moment(trimmedValue, this.dateFormat, true);
      
      if (parsedDate.isValid()) {
        // Update form control value in date picker format
        this.form.controls[fieldName].setValue({
          startDate: parsedDate,
          endDate: parsedDate
        });
      } else {
        // Set as invalid string and mark as touched to show error
        // Also restore the invalid value in the input
        this.form.controls[fieldName].setValue(trimmedValue);
        this.form.controls[fieldName].markAsTouched();
        // Restore the raw input value in the DOM
        event.target.value = trimmedValue;
      }
    } else if (trimmedValue.length > 0) {
      // Incomplete date - set as string
      this.form.controls[fieldName].setValue(trimmedValue);
      this.form.controls[fieldName].markAsTouched();
    }
    
    // Trigger validation
    this.form.controls[fieldName].updateValueAndValidity();
  }

  // private cancelEdit() {
  //   this.season = this.seasonCopy;
  //   this.setItemOptions(this.season);
  // }

  private setItemOptions(season, mode = '') {
    this.createMode = false;
    this.editMode = false;
    this.viewMode = true;

    if (mode === 'edit') {
      this.onEditedSeason();
    }

    if (this.season.to < this.today && this.season.status) {
      this.outDatedSeasons = true;
    }

    // Convert to date
    this.form.controls['from'].setValue({ startDate: this.dateService.format(this.season.from) });
    this.form.controls['to'].setValue({ startDate: this.dateService.format(this.season.to) });

    this.seasonCopy = { ...season };
  }

  private setSeasonForm() {
    this.form = this.formBuilder.group({
      name: [
        '',
        {
          validators: [
            Validators.required,
            // Validators.maxLength(100), used it in html to limit the input length
            // Validators.pattern(this.utilService.selectInputPattern('text')), // Problems with German characters
          ],
          updateOn: 'blur',
        },
      ],
      from: ['', [Validators.required, this.dateValidator(this.dateFormat)]],
      to: ['', [Validators.required, this.dateValidator(this.dateFormat)]],
    });
  }

  private setDatePickers() {
    this.calendarLocale = {
      format: this.dateFormat,
      firstDay: 1,
    };
  }
}
